import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { useDraggable } from "@/hooks/useDraggable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Per-user dismiss key so revoking/granting a different account isn't blocked
// by a stale 1h dismissal saved by another user on the same browser.
const dismissKey = (uid) => `ca_vidente_float_dismissed_until::${uid || "_"}`;

export const VidenteFloat = () => {
  const { isUser, isAdmin, token, user } = useAuth();
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(false);
  const [drawReady, setDrawReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const drag = useDraggable();

  const check = useCallback(async () => {
    if (!isUser || isAdmin) {
      setHasAccess(false);
      return;
    }
    try {
      const r = await axios.get(`${API}/vidente/access`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const next = !!r.data?.has_access;
      setHasAccess((prev) => {
        // Only clear a stale dismissal when access TRANSITIONS from
        // false → true (i.e. the admin just granted it). Doing this on
        // every successful poll would defeat the user's "hide for 1h" tap.
        if (next && !prev && user?.id) {
          try { localStorage.removeItem(dismissKey(user.id)); } catch { /* noop */ }
          setDismissed(false);
        }
        return next;
      });
      setDrawReady(!!r.data?.draw_ready);
    } catch {
      setHasAccess(false);
    }
  }, [isUser, isAdmin, token, user?.id]);

  useEffect(() => { check(); }, [check]);

  // Re-check when notifications come in (admin grants → notify_user triggers SSE)
  useRealtimeEvents({
    notification: (data) => {
      const cat = data?.category;
      if (cat === "vidente") check();
    },
    admin_refresh: (data) => {
      // Backend also publishes admin_refresh{scope:'users'|'vidente'} when
      // grants/revokes happen — re-check on any related signal.
      const scope = data?.scope;
      if (scope === "vidente" || scope === "users") check();
    },
  });

  // Poll more aggressively (10s) so the float appears within a few seconds
  // of an admin grant even if the SSE event is missed (sleeping tab, mobile
  // background, proxy buffering, etc.)
  useEffect(() => {
    if (!isUser || isAdmin) return;
    const t = setInterval(() => { check(); }, 10000);
    return () => clearInterval(t);
  }, [check, isUser, isAdmin]);

  // Re-check when the tab becomes visible again or the window regains focus
  // (covers the case where the user was on another tab while the admin
  // granted access).
  useEffect(() => {
    if (!isUser || isAdmin) return undefined;
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [check, isUser, isAdmin]);

  // Honor a tiny dismissal window so users can hide the float for 1h
  // (per-user key — different accounts on the same browser don't share it).
  useEffect(() => {
    try {
      const until = parseInt(localStorage.getItem(dismissKey(user?.id)) || "0", 10);
      setDismissed(Date.now() < until);
    } catch { /* noop */ }
  }, [user?.id]);

  // Hide on the page itself
  const onVidentePage = typeof window !== "undefined" && window.location.pathname.startsWith("/vidente-virtual");

  if (!isUser || isAdmin || !hasAccess || dismissed || onVidentePage) return null;

  const dismiss = (e) => {
    e.stopPropagation();
    try {
      localStorage.setItem(dismissKey(user?.id), String(Date.now() + 60 * 60 * 1000));
    } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="vidente-float"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        ref={drag.ref}
        onPointerDown={drag.onPointerDown}
        onPointerMove={drag.onPointerMove}
        onPointerUp={drag.onPointerUp}
        onPointerCancel={drag.onPointerCancel}
        data-draggable="true"
        style={drag.style}
        className="fixed left-6 bottom-[26rem] z-[62] cursor-grab active:cursor-grabbing"
      >
        <button
          onClick={() => navigate("/vidente-virtual")}
          className="group relative flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-gradient-to-r from-[#d4af37] via-amber-500 to-purple-600 text-black font-semibold shadow-[0_0_30px_rgba(212,175,55,0.55)] border-2 border-[#d4af37]/80 hover:scale-105 transition-all"
          title={drawReady ? "Cristopher dice: las cartas ya quieren hablar" : "Tu Vidente Virtual está habilitada"}
        >
          <span className="relative">
            <Mic className="w-4 h-4" />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-400 border border-black animate-pulse" />
          </span>
          <span className="text-xs uppercase tracking-widest whitespace-nowrap">
            Vidente Virtual
          </span>
          {drawReady ? (
            <span
              data-testid="vidente-float-draw-ready"
              className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black text-[#d4af37] text-[10px] font-bold uppercase tracking-widest border border-[#d4af37] animate-pulse"
            >
              🎴 listo
            </span>
          ) : (
            <span className="hidden sm:inline text-[10px] opacity-80 normal-case">
              · Habilitada
            </span>
          )}
        </button>
        <button
          onClick={dismiss}
          aria-label="Ocultar por 1 hora"
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-black/80 border border-white/30 text-neutral-300 hover:text-white flex items-center justify-center"
          data-testid="vidente-float-dismiss"
        >
          <X className="w-3 h-3" />
        </button>
      </motion.div>
    </AnimatePresence>
  );
};
